/**
 * MobileViewMissionModal
 *
 * MUI full-screen Dialog replacement for ViewMissionModal (PrimeReact Dialog).
 * Read-only view of mission details with mobile-friendly layout.
 *
 * Components from MUI: Dialog, AppBar, Toolbar, IconButton, Typography, TextField, Box, Slide
 */
import React from "react";
import moment from "moment";

// MUI Components
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  TextField,
  Box,
  Slide,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Shared utilities
import Langs from "../../../app/lang/langs";

// Shared components (reused as-is)
import { AssetsPannel } from "../../../components/inputs/AssetsPannel";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";
const BORDER_RADIUS_INPUT = "12px";

const fieldLabelSx = {
  fontWeight: 600,
  mb: 0.75,
  letterSpacing: "-0.01em",
  color: "text.secondary",
  fontSize: "0.82rem",
  textTransform: "uppercase",
};

const glassmorphicTextFieldSx = {
  mb: 3,
  "& .MuiOutlinedInput-root": {
    borderRadius: BORDER_RADIUS_INPUT,
    backgroundColor: "rgba(255,255,255,0.06)",
    backdropFilter: "blur(6px)",
    transition: TRANSITION_SMOOTH,
    "& fieldset": {
      borderColor: "rgba(0,0,0,0.08)",
      transition: TRANSITION_SMOOTH,
    },
    "&:hover fieldset": {
      borderColor: "rgba(0,0,0,0.18)",
    },
  },
  "& input": { fontSize: "16px", transition: TRANSITION_SMOOTH },
  "& input::placeholder": { opacity: 0.45 },
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function MobileViewMissionModal(props) {
  const mission = props.selectedMission;

  const handleClose = () => {
    props.onHide();
  };

  const formatDate = (date) => {
    return date ? moment(date).format("DD/MM/YYYY") : "";
  };

  const fields = [
    { key: "team_name", label: "TEAM" },
    { key: "poste_name", label: "POSTE" },
    { key: "start_date", label: "START_DATE", isDate: true },
    { key: "end_date", label: "END_DATE", isDate: true },
  ];

  return (
    <Dialog
      fullScreen
      open={!!mission}
      onClose={handleClose}
      TransitionComponent={Transition}
      sx={{
        zIndex: 1400,
        "& .MuiDialog-paper": {
          background: "var(--surface-ground, #f4f6f8)",
        },
      }}
    >
      {/* Top AppBar */}
      <AppBar
        sx={{
          position: "sticky",
          top: 0,
          bgcolor: "rgba(255,255,255,0.82)",
          color: "text.primary",
          boxShadow: "0 1px 12px rgba(0,0,0,0.06)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <Toolbar sx={{ py: 0.5 }}>
          <IconButton
            edge="start"
            onClick={handleClose}
            aria-label="close"
            sx={{
              transition: TRANSITION_SMOOTH,
              borderRadius: "12px",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.06)",
                transform: "scale(1.08)",
              },
              "&:active": { transform: "scale(0.95)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            sx={{
              ml: 1.5,
              flex: 1,
              fontWeight: 600,
              fontSize: "1.15rem",
              letterSpacing: "-0.02em",
            }}
            variant="h6"
          >
            <Langs str="VIEW_MISSION" />
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Content */}
      <Box
        sx={{
          p: 2.5,
          pb: 4,
          overflowY: "auto",
          flex: 1,
          "@keyframes fadeSlideIn": {
            from: { opacity: 0, transform: "translateY(10px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
          animation: "fadeSlideIn 0.35s cubic-bezier(0, 0, 0.2, 1) both",
        }}
      >
        {/* Fields Card */}
        <Box
          sx={{
            background: "rgba(255,255,255,0.65)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            borderRadius: "16px",
            border: "1px solid rgba(255,255,255,0.45)",
            boxShadow:
              "0 2px 16px rgba(0,0,0,0.04), 0 0.5px 2px rgba(0,0,0,0.03)",
            p: 2.5,
            mb: 2.5,
            transition: TRANSITION_SMOOTH,
          }}
        >
          {mission &&
            fields.map((field) => (
              <React.Fragment key={field.key}>
                <Typography variant="body2" sx={fieldLabelSx}>
                  <Langs str={field.label} />
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  disabled
                  value={
                    field.isDate
                      ? formatDate(mission[field.key])
                      : mission[field.key] || ""
                  }
                  sx={glassmorphicTextFieldSx}
                />
              </React.Fragment>
            ))}
        </Box>

        {/* Assets Section */}
        {mission && (
          <Box
            sx={{
              background: "rgba(255,255,255,0.65)",
              backdropFilter: "blur(16px)",
              WebkitBackdropFilter: "blur(16px)",
              borderRadius: "16px",
              border: "1px solid rgba(255,255,255,0.45)",
              boxShadow:
                "0 2px 16px rgba(0,0,0,0.04), 0 0.5px 2px rgba(0,0,0,0.03)",
              p: 2.5,
              transition: TRANSITION_SMOOTH,
            }}
          >
            <AssetsPannel
              tableName="team_members"
              idParent={mission.id_mission}
              assets={mission.assets}
              uploadCallBackFunction={() => {}}
              deleteCallBackFunction={() => {}}
              label="Assets"
              readOnly={true}
            />
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
